import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { API_BASE_URL, API_PREFIX } from '../config';

const AUTH_STORAGE_KEY = 'netdrive_token';

interface AuthContextValue {
  token: string | null;
  isAuthenticated: boolean;
  login: (password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

async function requestLogin(password: string): Promise<string> {
  const response = await fetch(`${API_BASE_URL}${API_PREFIX}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });

  if (!response.ok) {
    throw new Error('密码不正确');
  }

  const data = (await response.json()) as { token: string };
  return data.token;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(AUTH_STORAGE_KEY));

  useEffect(() => {
    if (token) {
      localStorage.setItem(AUTH_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  }, [token]);

  const login = useCallback(async (password: string) => {
    try {
      const newToken = await requestLogin(password);
      setToken(newToken);
      toast.success('登录成功');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '登录失败');
      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    toast('已退出登录');
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      isAuthenticated: Boolean(token),
      login,
      logout,
    }),
    [token, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth 必须在 AuthProvider 中使用');
  }

  return ctx;
}
